import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Spin, message } from "antd";
import { CheckOutlined, SearchOutlined } from "@ant-design/icons";
import API_MANAGER from "../../../API";

const getInitials = (name) => {
  if (!name) return "C";
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
};

const extractCompanyList = (payload) => {
  const candidates = [
    payload?.data?.data?.companies,
    payload?.data?.data?.result,
    payload?.data?.data,
    payload?.data?.companies,
    payload?.data?.result,
    payload?.data,
    payload,
  ];
  const match = candidates.find((value) => Array.isArray(value));
  return match || [];
};

const getNestedId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  const id = value._id || value.id;
  return id && typeof id !== "object" ? String(id) : "";
};

const getCompanyId = (item) => {
  return (
    getNestedId(item?.recruiterId) ||
    getNestedId(item?.company) ||
    getNestedId(item?.associatedCompany) ||
    getNestedId(item?.requestedToCompany) ||
    getNestedId(item)
  );
};

const getCompanyUserId = (item) =>
  getNestedId(item?.userId) || getNestedId(item?.user);

const readText = (value) =>
  typeof value === "string" ? value.trim() : "";

const toNameCase = (value) => {
  const text = readText(value);
  if (!text) return "";
  return text
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const getUserName = (item) => {
  const user = item?.userId || item?.user || {};
  const firstName = toNameCase(user?.firstName);
  const lastName = toNameCase(user?.lastName);
  return `${firstName} ${lastName}`.trim();
};

const getCompanyFields = (item) => {
  const source =
    item?.recruiterId ||
    item?.company ||
    item?.associatedCompany ||
    item?.requestedToCompany ||
    item;
  const tradingName =
    readText(source?.tradingName) || readText(item?.tradingName);
  const companyName =
    readText(source?.companyName) ||
    readText(item?.companyName) ||
    readText(source?.name) ||
    readText(item?.name);
  return { tradingName, companyName };
};

const normalizeCompanies = (payload) => {
  return extractCompanyList(payload)
    .map((item) => {
      const id = getCompanyId(item);
      if (!id) return null;
      const { tradingName, companyName } = getCompanyFields(item);
      const companyUserId = getCompanyUserId(item);
      return {
        id,
        companyUserId,
        selectionKey: `${id}-${companyUserId || "company"}`,
        name: tradingName || companyName || "Unknown Company",
        tradingName,
        companyName,
        userName: getUserName(item),
      };
    })
    .filter(Boolean);
};

const IncomingWorkforceRequestsModal = ({
  visible,
  onClose,
  advertId,
  applicationIds = [],
  onTransferSuccess,
}) => {
  const [companies, setCompanies] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const currentClientId = localStorage.getItem("clientId");

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await API_MANAGER.getAssociationCompanies();
      const normalized = normalizeCompanies(response).filter(
        (company) => company.id !== currentClientId
      );
      setCompanies(normalized);
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message?.message ||
        error?.response?.data?.message;
      message.error(
        (Array.isArray(apiMessage) ? apiMessage.join(", ") : apiMessage) ||
          "Failed to load companies. Please try again."
      );
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!visible) return;
    setSelectedKey(null);
    setSearch("");
    fetchCompanies();
  }, [visible]);

  const filteredCompanies = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return companies;
    return companies.filter((company) =>
      `${company.name} ${company.tradingName} ${company.companyName} ${company.userName}`
        .toLowerCase()
        .includes(query)
    );
  }, [companies, search]);

  const selectedCompany = companies.find(
    (company) => company.selectionKey === selectedKey
  );

  const handleConfirm = async () => {
    if (!selectedCompany?.id) {
      message.warning("Please select a company.");
      return;
    }
    if (!selectedCompany?.companyUserId) {
      message.error("Company user is missing.");
      return;
    }
    if (!advertId) {
      message.error("Advert id is missing.");
      return;
    }
    if (!applicationIds.length) {
      message.warning("Please select at least one candidate.");
      return;
    }

    setSubmitting(true);
    try {
      await API_MANAGER.createCompanyTransferRequest({
        applicationIds,
        advertId,
        requestedToCompany: selectedCompany.id,
        companyUserId: selectedCompany.companyUserId,
      });
      message.success("Transfer request successfully sent.");
      onTransferSuccess?.();
      onClose?.();
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message?.message ||
        error?.response?.data?.message;
      message.error(
        (Array.isArray(apiMessage) ? apiMessage.join(", ") : apiMessage) ||
          "Failed to create workforce transfer. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseAll = () => {
    if (submitting) return;
    setSelectedKey(null);
    onClose?.();
  };

  return (
    <Modal
      centered
      getContainer={document.body}
      title="Workforce Requests"
      className="requestWorkforceModal"
      open={visible}
      onCancel={handleCloseAll}
      footer={[
        <div
          key="footer"
          style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
        >
          <Button
            key="confirm"
            className="black_color_button"
            loading={submitting}
            onClick={handleConfirm}
          >
            Confirm
          </Button>
          <Button key="cancel" className="cancel_btn" onClick={handleCloseAll}>
            Cancel
          </Button>
        </div>,
      ]}
      width={480}
    >
      <p className="workforce-subtitle">
        Select a company to transfer the selected candidates.
      </p>
      <Input
        className="workforce-search"
        placeholder="Search company"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        prefix={<SearchOutlined style={{ color: "#1638B1" }} />}
      />
      <Spin spinning={loading}>
        <div className="workforce-list">
          {filteredCompanies.length > 0 ? (
            filteredCompanies.map((company) => {
              const isSelected = company.selectionKey === selectedKey;
              return (
                <div
                  key={company.selectionKey}
                  className={
                    isSelected ? "workforce-card active" : "workforce-card"
                  }
                  onClick={() => setSelectedKey(company.selectionKey)}
                >
                  <div className="workforce-avatar">
                    {getInitials(company.name)}
                  </div>
                  <div className="workforce-meta">
                    <div className="workforce-name">{company.name}</div>
                    <div className="workforce-hint">
                      {company.userName || "Company"}
                    </div>
                  </div>
                  <div className="workforce-check">
                    <CheckOutlined />
                  </div>
                </div>
              );
            })
          ) : (
            !loading && (
              <div className="workforce-empty">No companies found</div>
            )
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default IncomingWorkforceRequestsModal;
